import { useState, useEffect } from "react";
import axios from "axios";
import { Play, Pause, Disc3 } from "lucide-react";
import { useAudio } from "../contexts/AudioContext";
import { API, COVER_IMAGE, ALBUM_COVER } from "../lib/constants";

const ALBUM_DATA = {
  "Notary Hill G.N.W.": {
    header: "CLUB T.I.N.G. PRESENTS", title: "NOTARY HILL", titleAccent: "G.N.W.",
    subtitle: "Executive Producer: LEILA\\N.", label: "CLUB T.I.N.G. \u00b7 2026", coverImage: ALBUM_COVER
  },
  "The Rewrite Vol.1: The Grind": {
    header: "SEASON 1 \u2014 ORIGINAL SCORE", title: "THE GRIND", titleAccent: "THE ASCENSION",
    subtitle: "The sound of the hustle, the first stamp, the awakening", label: "Notary Hill \u00b7 2026", coverImage: COVER_IMAGE
  },
  "The Rewrite Vol.2: The Moral Gray": {
    header: "SEASON 2 \u2014 ORIGINAL SCORE", title: "THE MORAL GRAY", titleAccent: "THE VOID WAR",
    subtitle: "The collapse of certainty, the battle within", label: "Notary Hill \u00b7 2026", coverImage: COVER_IMAGE
  },
  "The Rewrite Vol.3: The Pivot": {
    header: "SEASON 3 \u2014 ORIGINAL SCORE", title: "THE PIVOT", titleAccent: "THE NEW UNIVERSE",
    subtitle: "The reforging, the alignment, the turning point", label: "Notary Hill \u00b7 2026", coverImage: COVER_IMAGE
  },
  "The Rewrite Vol.4: The Kingdom": {
    header: "SEASON 4 \u2014 ORIGINAL SCORE", title: "THE KINGDOM", titleAccent: "THE FINAL INTEGRATION",
    subtitle: "The new dawn, the world that sings again", label: "Notary Hill \u00b7 2026", coverImage: COVER_IMAGE
  }
};

export default function MusicPage() {
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState("Notary Hill G.N.W.");
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const { playTrack, currentTrack, isPlaying } = useAudio();

  useEffect(() => {
    const fetchAlbums = async () => {
      try {
        const res = await axios.get(`${API}/music/albums`);
        setAlbums(res.data);
        if (res.data.length > 0 && !res.data.find(a => a.name === selectedAlbum)) {
          setSelectedAlbum(res.data[0].name);
        }
      } catch (e) { console.error(e); }
    };
    fetchAlbums();
  }, []);

  useEffect(() => {
    const fetchTracks = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/music?album=${encodeURIComponent(selectedAlbum)}`);
        setTracks(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchTracks();
  }, [selectedAlbum]);

  const albumInfo = ALBUM_DATA[selectedAlbum] || ALBUM_DATA["Notary Hill G.N.W."];

  const renderTrackList = () => {
    if (loading) return (<div className="p-8 text-center"><div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>);
    if (tracks.length === 0) return (<div className="p-8 text-center"><Disc3 size={48} className="mx-auto mb-4 text-gray-700" /><p className="text-gray-500">No tracks yet</p><p className="text-sm text-gray-600 mt-2">Tracks are managed in the Asset Manager</p></div>);

    return tracks.map((track) => {
      const isActive = currentTrack?.id === track.id;
      return (
        <div key={track.id} className={`track-item cursor-pointer ${isActive ? 'playing' : ''}`} onClick={() => track.audio_url && playTrack(track)} data-testid={`track-${track.id}`}>
          <span className="font-mono text-gray-500 w-8">{String(track.track_number).padStart(2, '0')}</span>
          <div className="flex-1 min-w-0">
            <p className="font-manrope truncate">{track.title}</p>
            <p className="text-sm text-gray-500 truncate">{track.artist}</p>
          </div>
          {track.audio_url ? (isActive && isPlaying ? <Pause size={18} className="text-red-500" /> : <Play size={18} className="text-gray-500" />) : <span className="text-xs text-gray-600">No audio</span>}
        </div>
      );
    });
  };

  return (
    <div className="min-h-screen pt-24 pb-32 px-6" data-testid="music-page">
      <div className="max-w-5xl mx-auto">
        {albums.length === 0 ? (
          <div className="text-center pt-20"><Disc3 size={64} className="mx-auto mb-6 text-gray-700" /><h2 className="font-cinzel text-2xl mb-3">Coming Soon</h2><p className="text-gray-500">Music albums are being prepared for release.</p></div>
        ) : (
        <>
        <div className="flex justify-center gap-2 mb-10 flex-wrap" data-testid="album-tabs">
          {albums.map(a => (
            <button key={a.name} onClick={() => setSelectedAlbum(a.name)}
              className={`px-5 py-2.5 text-sm font-mono tracking-wider rounded transition-all ${selectedAlbum === a.name ? 'bg-red-600 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'}`}
              data-testid={`album-tab-${a.name}`}>{a.name}</button>
          ))}
        </div>

        <header className="mb-12 text-center">
          <p className="font-mono text-red-500 text-sm tracking-[0.3em] mb-4">{albumInfo.header}</p>
          <h1 className="font-cinzel text-4xl md:text-5xl font-bold">{albumInfo.title} <span className="text-red-500">{albumInfo.titleAccent}</span></h1>
        </header>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div className="flex flex-col items-center">
            <div className={`vinyl-record ${isPlaying ? 'vinyl-spin' : 'vinyl-spin paused'}`}>
              <div className="absolute inset-[15%] rounded-full bg-cover bg-center" style={{ backgroundImage: `url(${albumInfo.coverImage})` }} />
            </div>
            <div className="mt-6 text-center">
              <p className="text-gray-500 text-sm">{albumInfo.subtitle}</p>
              <p className="text-xs text-gray-600 mt-1">{albumInfo.label}</p>
              {tracks.length > 0 && <p className="text-xs text-red-500/70 mt-2">{tracks.length} track{tracks.length !== 1 ? 's' : ''}</p>}
            </div>
          </div>
          <div className="glass-dirty max-h-[600px] overflow-y-auto">{renderTrackList()}</div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
